import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useEffect, useState } from "react";
import { useGetCollarById } from "@services/device/collar/getById";
import Heading from "@commons/Typography/Heading";
import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import ListPreview from "@commons/Preview/ListPreview";
import { formatDateTime } from "@utils/formatters";
import Text from "@commons/Typography/Text";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { getSituationLabel } from "@utils/translator/individuals";
import Button from "@commons/Button";
import { globalMutate } from "@utils/swr";
import { usePage } from "@hooks/usePage";
import ModalUpdateCollarIndividual from "./ModalUpdateCollarIndividual";
import ModalEditCollar from "./ModalEditCollar";
import { updateCollarIndividual } from "@services/device/collar/updateIndividual";
import { AppError } from "@utils/errors/appError";
import CollarMapTabs from "./CollarMapTabs";
import { usePermissions } from "@hooks/usePermissions";

function ViewCollar() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [openDelete, setOpenDelete] = useState(false);
  const { openModal, setDataModal, setIsLoading } = usePage();
  const { can } = usePermissions();

  const { data: collar, isLoading, error } = useGetCollarById(id ?? null);

  useEffect(() => {
    if (error) {
      toast.error("Erro ao carregar os dados do Rastreador.");
      navigate("/devices");
    }
  }, [error, navigate]);

  useEffect(() => {
    if (!isLoading && !collar && !error) {
      toast.warning("Rastreador não encontrado.");
      navigate("/devices");
    }
  }, [isLoading, collar, error, navigate]);

  if (!collar) return null;

  const handleDelete = async () => {
    try {
      setIsLoading(true);

      await updateCollarIndividual({
        id: collar.id,
        individualId: null,
      });

      setOpenDelete(false);
      toast.success("Indivíduo desassociado com sucesso!");
      await globalMutate([`fetch-collar-${id}`]);
    } catch (error) {
      const isAppError = error instanceof AppError;
      const title = isAppError
        ? error.message
        : "Erro ao desassociar indivíduo";
      toast.error(title);
    } finally {
      setOpenDelete(false);
      setIsLoading(false);
    }
  };

  const handleUpdateIndividual = () => {
    setDataModal({ collarId: collar.id });
    openModal("updateCollarIndividual");
  };

  const previewRows = [
    { label: "Número:", value: collar.number },
    { label: "Latitude:", value: collar.lastLocationLatitude },
    { label: "Longitude:", value: collar.lastLocationLongitude },
    { label: "Equipe Responsável:", value: collar.responsibleTeam },
    { label: "Última Atualização:", value: formatDateTime(collar.updatedAt) },
    { label: "Última Edição por:", value: collar.lastEditor },
  ];

  const individualPreviewRows = collar.individual
    ? [
        { label: "Nº:", value: collar.individual.clinicalRecord },
        {
          label: "Data de Entrada:",
          value: formatDateTime(collar.individual.lastEntranceDate),
        },
        { label: "Nome UFU:", value: collar.individual.ufuName },
        { label: "Espécie:", value: collar.individual.taxonomicGroup.species },
        {
          label: "Situação:",
          value: getSituationLabel(collar.individual.situation),
        },
      ]
    : [];

  const canUpdateCollar = can("update", "collars");

  return (
    <section className="flex flex-col gap-2 items-start w-full bg-white justify-start p-4 sm:p-6 shadow-md rounded-2xl">
      <div className="w-full flex justify-between items-center gap-4 mb-4">
        <Heading>Rastreador</Heading>
        <div className="flex gap-2">
          {canUpdateCollar && (
            <Button
              customVariant="muted"
              size="auto"
              onClick={() => {
                setDataModal({ id: collar.id });
                openModal("editCollar");
              }}
            >
              <Text size="sm">Editar Rastreador</Text>
            </Button>
          )}
          <Button
            customVariant="muted"
            size="auto"
            onClick={() => navigate("/devices")}
          >
            <Text size="sm">Voltar</Text>
          </Button>
        </div>
      </div>
      <div className="w-full">
        {isLoading ? (
          <div className="flex justify-center items-center h-64">
            <CircularProgress size={60} className="text-primary" />
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-stretch">
            <div className="flex flex-col gap-2 items-start w-full h-full justify-start rounded-2xl">
              <ListPreview.Root>
                <Heading size="sm">Informações Gerais:</Heading>
                {previewRows.map((row) => (
                  <ListPreview.Row
                    key={row.label}
                    label={row.label}
                    value={row.value}
                  />
                ))}
              </ListPreview.Root>
            </div>
            <div className="flex flex-col justify-start w-full h-full rounded-2xl">
              {collar.individual ? (
                <ListPreview.Root className="h-full">
                  <div className="w-full flex justify-between mb-2">
                    <Heading size="sm">Indivíduo Associado</Heading>
                    {canUpdateCollar && (
                      <div className="flex gap-2">
                        <IconButton
                          className="h-8 w-8 bg-primary hover:bg-primary/80"
                          aria-label="Editar"
                          onClick={handleUpdateIndividual}
                        >
                          <EditIcon className="text-[18px] text-white" />
                        </IconButton>
                        <IconButton
                          className="h-8 w-8 bg-red-600 hover:bg-red-400"
                          aria-label="Deletar"
                          onClick={() => setOpenDelete(true)}
                        >
                          <DeleteIcon className="text-[18px] text-white" />
                        </IconButton>
                      </div>
                    )}
                  </div>
                  {individualPreviewRows.map((row) => (
                    <ListPreview.Row
                      key={row.label}
                      label={row.label}
                      value={row.value}
                    />
                  ))}
                </ListPreview.Root>
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center py-12 text-gray-400 border border-gray-200 rounded-md bg-gray-50 gap-2">
                  <Text size="md" className="font-medium">
                    Nenhum indivíduo associado
                  </Text>
                  {canUpdateCollar && (
                    <Button onClick={handleUpdateIndividual}>
                      <Text>Associar Indivíduo</Text>
                    </Button>
                  )}
                </div>
              )}
            </div>

            <CollarMapTabs
              latitude={collar.lastLocationLatitude}
              longitude={collar.lastLocationLongitude}
            />
          </div>
        )}
      </div>

      <Dialog open={openDelete} onClose={() => setOpenDelete(false)}>
        <DialogTitle>Confirmar Desassociação</DialogTitle>
        <DialogContent>
          <Text>Tem certeza que deseja deletar essa associação?</Text>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDelete(false)} customVariant="dark">
            <Text>Cancelar</Text>
          </Button>
          <Button onClick={handleDelete} customVariant="danger">
            <Text>Deletar</Text>
          </Button>
        </DialogActions>
      </Dialog>

      <ModalUpdateCollarIndividual />
      <ModalEditCollar />
    </section>
  );
}

export default ViewCollar;
